import React, { useEffect, useState } from 'react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  LineChart, Line, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import {
  FaUsers, FaChartLine, FaBriefcase, FaHandshake, FaRegCalendarAlt,
} from 'react-icons/fa';
import { MdBusinessCenter } from 'react-icons/md';
import { AiOutlineSolution } from 'react-icons/ai';
import axios from 'axios';
import DashboardSolution from '../components/Dashboardsolution';
import FundingTable from '../components/FundingTable';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#0ea5e9', '#a855f7', '#f43f5e'];

const iconList = [
  <FaUsers />, <FaHandshake />, <FaBriefcase />, <FaChartLine />,
  <MdBusinessCenter />, <FaRegCalendarAlt />, <AiOutlineSolution />, <AiOutlineSolution />,
];

const gradients = [
  'from-purple-500 to-indigo-500',
  'from-emerald-400 to-teal-500',
  'from-yellow-400 to-orange-500',
  'from-red-400 to-pink-500',
  'from-sky-400 to-blue-500',
  'from-fuchsia-400 to-violet-500',
  'from-pink-500 to-rose-500',
];

const Ecommerce = () => {
  const [stats, setStats] = useState({
    startupCount: 0,
    cofounderCount: 0,
    businessCount: 0,
    consultationCount: 0,
    careerCount: 0,
    eventCount: 0,
    solutionCount: 0,
    fundingCount: 0
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/stats`);
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };

    fetchStats();
  }, []);

  const pieData = Object.entries(stats).map(([key, value], index) => ({
    name: key.replace('Count', ''),
    value,
    color: COLORS[index % COLORS.length],
  }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6 md:p-10 font-sans text-gray-800">
      <h1 className="text-4xl md:text-5xl font-extrabold mb-10 tracking-tight text-gray-800">
        Admin Dashboard
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* LEFT MAIN COLUMN */}
        <div className="col-span-2 space-y-8">

          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
            {pieData.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl p-5 shadow-xl flex items-center gap-4 transition hover:shadow-2xl border border-gray-100"
              >
                <div
                  className={`w-14 h-14 flex items-center justify-center rounded-full text-white text-xl bg-gradient-to-br ${gradients[index % gradients.length]}`}
                >
                  {iconList[index]}
                </div>
                <div>
                  <p className="text-sm text-gray-500 capitalize">{item.name}</p>
                  <p className="text-2xl font-semibold">{item.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Dashboard Solution */}
          <div className="bg-white rounded-3xl p-6 shadow-xl border border-gray-100">
            <h2 className="text-lg font-semibold mb-4">Dashboard Solution</h2>
            <div className="max-h-[300px] overflow-y-auto pr-2">
              <DashboardSolution />
            </div>
          </div>

          {/* Funding Table */}
          <div className="bg-white rounded-3xl p-6 shadow-xl border border-gray-100">
            <h2 className="text-lg font-semibold mb-4">Latest Fundings</h2>
            <div className="max-h-[320px] overflow-y-auto pr-2">
              <FundingTable />
            </div>
          </div>
        </div>

        {/* RIGHT CHART COLUMN */}
        <div className="space-y-8">
          {/* Line Chart */}
          {/* <div className="bg-white rounded-3xl p-6 shadow-xl border border-gray-100">
            <h3 className="text-lg font-semibold mb-4">Business Overview</h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={pieData}>
                <defs>
                  <linearGradient id="lineColor" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#6366f1" />
                    <stop offset="100%" stopColor="#a855f7" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="url(#lineColor)"
                  strokeWidth={3}
                  dot={{ r: 5 }}
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div> */}
          <div className="rounded-3xl p-6 shadow-xl border border-gray-800 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
            <h3 className="text-lg font-extrabold mb-4 bg-gradient-to-r from-cyan-400 to-violet-500 text-transparent bg-clip-text">
              Business Overview
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={pieData}>
                <defs>
                  <linearGradient id="lineColor" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#38bdf8" />
                    <stop offset="100%" stopColor="#818cf8" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#4b5563" />
                <XAxis dataKey="name" stroke="#d1d5db" />
                <YAxis stroke="#d1d5db" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: 'none', color: '#fff' }}
                  itemStyle={{ color: '#fff' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="url(#lineColor)"
                  strokeWidth={3}
                  dot={{ r: 4, fill: '#fff' }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Pie Chart */}
          <div className="bg-white rounded-3xl p-6 shadow-xl border border-gray-100">
            <h3 className="text-lg font-semibold mb-4">Category Distribution</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <defs>
                  {pieData.map((entry, index) => (
                    <linearGradient id={`grad-${index}`} key={index} x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor={entry.color} stopOpacity={0.7} />
                      <stop offset="100%" stopColor={entry.color} stopOpacity={1} />
                    </linearGradient>
                  ))}
                </defs>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  fill="#8884d8"
                  dataKey="value"
                  isAnimationActive={true}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={`url(#grad-${index})`} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>

            <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
              {pieData.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 inline-block rounded-full"
                    style={{ backgroundColor: item.color }}
                  ></span>
                  <span className="capitalize">{item.name}</span> - <span>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ecommerce;